/**
 * Бэкенд отдаёт время в UTC ISO, а игровые сутки живут в Europe/Moscow
 * (CLAUDE.md бэкенда, раздел «Время»). Форматируем всегда в МСК, чтобы
 * игрок в другом часовом поясе видел то же расписание, что и правила игры.
 */
const MOSCOW_TIME_ZONE = 'Europe/Moscow';

const timeFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: MOSCOW_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
});

const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: MOSCOW_TIME_ZONE,
  day: 'numeric',
  month: 'long',
});

export function formatMoscowTime(iso: string): string {
  return timeFormatter.format(new Date(iso));
}

export function formatMoscowDate(iso: string): string {
  return dateFormatter.format(new Date(iso));
}

export function formatMoscowDateTime(iso: string): string {
  return `${formatMoscowDate(iso)}, ${formatMoscowTime(iso)}`;
}

/**
 * Название месяца по-русски: родительный («итоги сентября»), предложный
 * («в сентябре») или именительный («за сентябрь»).
 *
 * Таблицей, а не через `Intl`: тот отдаёт только родительный, а подписям
 * нужны все три — «удержишь позицию в октябре?» и «в розыгрыше
 * за сентябрь». Плюс это единственное место, где вообще разрешено
 * локальное форматирование (инвариант `locale-time`).
 *
 * Именительный годится и там, где по грамматике нужен винительный:
 * у всех месяцев эти формы совпадают, отдельной таблицы не требуется.
 */
const MONTHS_NOMINATIVE = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

const MONTHS_PREPOSITIONAL = [
  'январе',
  'феврале',
  'марте',
  'апреле',
  'мае',
  'июне',
  'июле',
  'августе',
  'сентябре',
  'октябре',
  'ноябре',
  'декабре',
];

const monthFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: MOSCOW_TIME_ZONE,
  month: 'numeric',
});

const MONTH_TABLES = {
  nominative: MONTHS_NOMINATIVE,
  genitive: MONTHS_GENITIVE,
  prepositional: MONTHS_PREPOSITIONAL,
};

export function formatMoscowMonth(
  iso: string,
  form: keyof typeof MONTH_TABLES = 'genitive',
): string {
  // Номер месяца берём в МСК: 1 октября 00:30 по Москве — это 30 сентября
  // по UTC, и без часового пояса подпись врала бы на границе
  const index = Number(monthFormatter.format(new Date(iso))) - 1;
  return MONTH_TABLES[form][index] ?? '';
}

/**
 * Календарный день по Москве как порядковый номер — чтобы вычесть один
 * из другого и получить «сегодня / завтра / потом».
 *
 * Через `formatToParts`, а не вычитанием миллисекунд: разница «меньше
 * 24 часов» и «тот же календарный день» — разные вещи. Матч в 23:40
 * и «сейчас» 00:10 разделяют полчаса, но дни уже разные, и подпись
 * «Сегодня» на завтрашнем матче врала бы ровно в тот момент, когда
 * игроку важнее всего не перепутать.
 */
const dayPartsFormatter = new Intl.DateTimeFormat('ru-RU', {
  timeZone: MOSCOW_TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

function moscowDayNumber(date: Date): number {
  const parts = dayPartsFormatter.formatToParts(date);
  const part = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return (
    Date.UTC(part('year'), part('month') - 1, part('day')) / 86_400_000
  );
}

/**
 * Заголовок карточки матча: «Сегодня в 22:40», «Завтра в 22:40»,
 * «14 сентября в 22:40».
 *
 * «Сегодня» и «Завтра» считаются по МОСКОВСКИМ суткам, а не по часам
 * устройства: игровой день определён в МСК, и игрок из Владивостока
 * должен видеть то же расписание, что и правила игры.
 */
export function formatMoscowRelativeDateTime(
  iso: string,
  now: Date = new Date(),
): string {
  const at = new Date(iso);
  const diffDays = moscowDayNumber(at) - moscowDayNumber(now);
  const time = formatMoscowTime(iso);

  if (diffDays === 0) return `Сегодня в ${time}`;
  if (diffDays === 1) return `Завтра в ${time}`;
  return `${formatMoscowDate(iso)} в ${time}`;
}

/** «через 2 ч 15 мин» / «приём закрыт» — для отсечки Прогнозов. */
export function formatTimeLeft(iso: string, now: Date = new Date()): string {
  const diffMs = new Date(iso).getTime() - now.getTime();
  if (diffMs <= 0) return 'приём закрыт';

  const totalMinutes = Math.floor(diffMs / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours >= 24) return `через ${String(Math.floor(hours / 24))} д`;
  if (hours > 0) return `через ${String(hours)} ч ${String(minutes)} мин`;
  return `через ${String(minutes)} мин`;
}
